import { ThemeToggle } from "@/components/theme/theme"
import { useGlobal } from "@/app/context/GlobalContext"
import { MODEL_OPTIONS, EFFORT_OPTIONS } from "@/lib/models"
import type { AnalystModelId, AnalystEffort } from "@/lib/models"
import { copy } from "@/lib/copy"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
// OCESA logo is a light mark; the header is the black OCESA nav in both modes,
// so the same asset reads on it. (White-knockout variant would go here.)
import logoWhite from "@/assets/brand/ocesa-logo.png"

// Header controls sit on the black OCESA nav, so the trigger uses
// header-foreground (light) tints rather than the page foreground/accent tokens.
// The open menu is a normal popover (bg-popover) so its items get full contrast
// — unlike the old native <select>, whose option list the OS painted (illegible
// gray + system-blue highlight).
const TRIGGER_CLASS =
  "h-auto gap-1.5 rounded-md border-header-foreground/25 bg-transparent px-2.5 py-1 text-xs font-medium text-header-foreground hover:bg-header-foreground/10 focus-visible:ring-ring data-[placeholder]:text-header-foreground [&_svg]:text-header-foreground/60"

type ChatHeaderProps = Record<string, never>

/**
 * Top app bar: a sidebar toggle and the app logo on the left; the theme toggle
 * on the right. New-chat and sign-out live in the sidebar. The logo swaps to the
 * white knockout in dark mode.
 *
 * @param onToggleSidebar - toggles the collapsible sidebar.
 * @param sidebarOpen - current sidebar state (for the toggle's aria-pressed).
 */
export function ChatHeader(_props: ChatHeaderProps) {
  const { selectedModel, setSelectedModel, selectedEffort, setSelectedEffort, isLoading } =
    useGlobal()

  return (
    <header className="flex w-full items-center justify-between border-b border-header-foreground/15 bg-header px-4 py-2.5 text-header-foreground">
      <div className="flex items-center gap-2">
        <img
          src={logoWhite}
          alt={copy.app.title}
          className="ml-3 h-9 w-auto"
        />
      </div>

      <div className="flex items-center gap-1.5">
        {/* Model selector — shadcn Select (Radix) so the open menu is fully
            styleable and accessible (keyboard, ARIA). */}
        <Select
          value={selectedModel}
          onValueChange={v => setSelectedModel(v as AnalystModelId)}
          disabled={isLoading}
        >
          <SelectTrigger className={TRIGGER_CLASS} aria-label={copy.models.selectLabel}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MODEL_OPTIONS.map(opt => (
              <SelectItem key={opt.id} value={opt.id}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Effort selector — controls the model's reasoning effort per turn. */}
        <Select
          value={selectedEffort}
          onValueChange={v => setSelectedEffort(v as AnalystEffort)}
          disabled={isLoading}
        >
          <SelectTrigger className={TRIGGER_CLASS} aria-label={copy.effort.selectLabel}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EFFORT_OPTIONS.map(opt => (
              <SelectItem key={opt.id} value={opt.id}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ThemeToggle className="!text-header-foreground/70 hover:!bg-header-foreground/10 hover:!text-header-foreground" />
      </div>
    </header>
  )
}
